import React from 'react';
import { Button } from '@/components/ui/button';
import { LifeBuoy } from 'lucide-react';
import { rand, monthLabel, dateLabel, StatusPill } from './format';

const Stat = ({ label, value, testid, accent, hint }) => (
  <div className={`rounded-xl border p-4 ${accent ? 'border-amber-300 bg-amber-50' : 'border-stone-200'}`}>
    <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
    <div className="text-2xl font-bold text-stone-800 mt-1" data-testid={testid}>{value}</div>
    {hint && <div className="text-[11px] text-stone-500 mt-0.5">{hint}</div>}
  </div>
);

const COLS = [['contribution', 'Deduction'], ['company', 'Company'], ['interest', 'Interest'], ['withdrawal', 'Withdrawal'], ['penalty', 'Penalty'], ['refund', 'Refund']];

export const StokvelMemberView = ({ data, onWithdraw }) => {
  const { membership: m, summary: s, withdrawals, categories } = data;
  const t = s.totals || {};
  const canWithdraw = m.status === 'active' && !s.emergency_used && s.emergency_max > 0;
  const cols = COLS.filter(([k]) => s.months.some(r => r[k]));
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-sm text-stone-700">
        <StatusPill status={m.status} testid="stokvel-membership-status" />
        <span>Monthly deduction <b>{rand(m.monthly_amount)}</b>{m.first_month && <> · from the {monthLabel(m.first_month)} payroll</>}</span>
        {m.end_date && <span>· ended {dateLabel(m.end_date)}</span>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={`Capital contributed ${s.year}`} value={rand(t.contribution)} testid="stokvel-capital" hint={t.withdrawal ? `less ${rand(t.withdrawal)} withdrawn` : null} />
        <Stat label="Company contributions" value={rand(t.company)} testid="stokvel-company" hint="Vests on the December payout date" />
        <Stat label="Interest earned" value={rand(t.interest)} testid="stokvel-interest" hint={s.forfeited ? `${rand(s.forfeited)} forfeited` : 'Pro rata to your balance'} />
        <Stat label="Indicative payout" value={rand(s.indicative_payout)} testid="stokvel-balance" accent hint={s.pot_share ? `incl. ${rand(s.pot_share)} penalty-pot share` : null} />
      </div>

      <div>
        <h3 className="font-semibold text-stone-800 mb-2">Monthly statement</h3>
        {s.months.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stokvel-schedule-table">
              <thead className="bg-stone-100 text-xs text-stone-600 uppercase">
                <tr><th className="text-left px-3 py-2">Payroll month</th>{cols.map(([k, l]) => <th key={k} className="text-right px-3 py-2">{l}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {s.months.map(r => (
                  <tr key={r.month} data-testid={`stokvel-month-${r.month}`}>
                    <td className="px-3 py-2">{monthLabel(r.month)}</td>
                    {cols.map(([k]) => (
                      <td key={k} className={`px-3 py-2 text-right font-mono ${['withdrawal', 'penalty', 'refund'].includes(k) && r[k] ? 'text-rose-700' : ''}`}>
                        {r[k] ? rand(r[k]) : <span className="text-stone-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-stone-500" data-testid="stokvel-no-contributions">{m.status === 'ended' ? 'No deductions were made this cycle.' : 'No deductions yet this cycle — your first one is on the 25th of your first payroll month.'}</p>}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="font-semibold text-stone-800">Emergency withdrawals</h3>
          <Button size="sm" variant="outline" disabled={!canWithdraw} onClick={onWithdraw} data-testid="stokvel-emergency-btn">
            <LifeBuoy className="w-4 h-4 mr-1" /> Request emergency payout
          </Button>
        </div>
        {s.emergency_used && <p className="text-xs text-stone-500 mb-2">You have already used your one emergency withdrawal for this cycle.</p>}
        {withdrawals.length ? (
          <div className="space-y-2">
            {withdrawals.map(w => (
              <div key={w.id} className="border border-stone-200 rounded-lg p-3 text-sm" data-testid={`stokvel-wd-${w.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill status={w.status} />
                  <b>{rand(w.status === 'approved' ? w.approved_amount : w.amount_requested)}</b>
                  <span className="text-stone-500">· {categories[w.category]} · requested {dateLabel(w.created_at)}</span>
                </div>
                {w.status === 'approved' && <p className="text-xs text-emerald-700 mt-1">Payout on {dateLabel(w.payout_date)}{w.decision_reason ? ` — ${w.decision_reason}` : ''}</p>}
                {w.status === 'declined' && <p className="text-xs text-rose-700 mt-1">Declined: {w.decision_reason}</p>}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-stone-500">No emergency withdrawals.</p>}
      </div>
    </div>
  );
};
